// Frontend mirror of the backend's mailbox pro-rata billing
// (manager-backend MailboxController::emitMailboxProRataEvent()). Replicated VERBATIM so the
// estimate shown in the create-mailbox modal equals the invoice line the backend emits.
//
// A mailbox is billed for the started "anchor-months" from today until the domain's yearly mail
// renewal anchor (MailDomain.next_billing_at), at the MAILBOX yearly unit price:
//   line = round(startedMonths / 12, 4) × yearly unit_price      (excl. VAT)
//
// Billing applies ONLY when mail billing is active for the domain (anchor set) AND the domain is on
// the shared IMAP server (netbox_imap_server_id === null). Dedicated-server / inactive domains are
// not billed per mailbox. If today is on/after the anchor, the upcoming anniversary bill already
// covers this mailbox, so no pro-rata charge is emitted at creation.

export type MailboxBillingClass =
  | { kind: 'not-billed' }
  | { kind: 'deferred'; anchor: string }
  | { kind: 'prorata'; startedMonths: number; anchor: string };

export function classifyMailboxBilling(
  nextBillingAt: string | null,
  imapServerId: number | null,
  today: Date = new Date(),
): MailboxBillingClass {
  // Gate: active anchor + shared IMAP only. A non-null anchor on a dedicated binding is a backend
  // "inconsistency" that is never billed — treat it as not-billed here too (safe under-billing).
  if (nextBillingAt === null || imapServerId !== null) {
    return { kind: 'not-billed' };
  }

  const parts = nextBillingAt.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
    return { kind: 'not-billed' };
  }
  const [anchorYear, anchorMonth, anchorDay] = parts;

  // Today as a local calendar date — mirrors the backend's date('Y-m-d').
  const createdYear = today.getFullYear();
  const createdMonth = today.getMonth() + 1;
  const createdDay = today.getDate();

  // created >= anchor: covered by the pending anniversary bill — no pro-rata at creation.
  const createdNum = createdYear * 10000 + createdMonth * 100 + createdDay;
  const anchorNum = anchorYear * 10000 + anchorMonth * 100 + anchorDay;
  if (createdNum >= anchorNum) {
    return { kind: 'deferred', anchor: nextBillingAt };
  }

  // periodStart = anchor − 1 year, matching PHP's `$anchor->modify('-1 year')` INCLUDING its
  // day-overflow normalization: a Feb-29 anchor becomes Mar 1 in the (non-leap) prior year. Build a
  // real Date (JS overflow-normalizes Feb 29 → Mar 1 identically) and read back the normalized
  // fields, so the month/day fed into monthsDiff equal the backend's. Day-aware month difference:
  // subtract one when the created day precedes the period-start day (anchors are creation DATES, so
  // mid-month is the norm and a naive calendar-month diff would undercount by one).
  const periodStartDate = new Date(anchorYear - 1, anchorMonth - 1, anchorDay);
  const periodStartYear = periodStartDate.getFullYear();
  const periodStartMonth = periodStartDate.getMonth() + 1;
  const periodStartDay = periodStartDate.getDate();
  const monthsDiff =
    (createdYear - periodStartYear) * 12 +
    (createdMonth - periodStartMonth) -
    (createdDay < periodStartDay ? 1 : 0);
  const startedMonths = 12 - monthsDiff;

  // Out of [1,12] cannot occur for created < anchor within a one-year period; if it somehow does,
  // don't assert a wrong amount — fall back to the deferred message (the backend asserts here).
  if (startedMonths < 1 || startedMonths > 12) {
    return { kind: 'deferred', anchor: nextBillingAt };
  }

  return { kind: 'prorata', startedMonths, anchor: nextBillingAt };
}

// Fraction-of-year billed: round(startedMonths / 12, 4) — the backend's quantity. Multiply by the
// yearly unit price for the line amount.
export function proRataQuantity(startedMonths: number): number {
  return Math.round((startedMonths / 12) * 10000) / 10000;
}
